import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';


import Modal from '../../../components/modal';
import Input from '../../../components/input';
import TextArea from '../../../components/textArea';
import Button from '../../../components/button';
import SearchableSelect from '../../../components/searchableSelect';
import Spinner from '../../../components/spinner';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';

import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import { listVpnEndpointGroups } from '../../../hooks/local/userReducer';
import { showSuccessMessage, showErrorMessage } from '../../../hooks/constants';
import { useSubnetList } from '../layout/resuableEffect';

const endpointTypeOptions = ['subnet', 'cidr', 'network', 'router', 'vlan'].map((v) => ({
  value: v,
  label: v,
}));

const VpnEndpointGroups = () => {
  document.title = 'VPN Endpoint Groups - VPN';

  const projectId = useSelector((state) => state.user.selectedProject?.id);
  const dispatch = useDispatch();
  const endpointGroups = useSelector((state) => state.user.endpointGroups);

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: '', name: '' });

  const subnetList = useSubnetList(projectId, '');
  const subnetOptions = useMemo(() => {
    const arr = Array.isArray(subnetList) ? subnetList : [];
    return arr.map((s) => ({
      value: s.id,
      label: s.name?.trim() ? `${s.name} (${s.cidr})` : `${s.id} (${s.cidr})`,
    }));
  }, [subnetList]);

  const refetch = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      await dispatch(listVpnEndpointGroups(projectId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const filtered = useMemo(() => {
    const arr = Array.isArray(endpointGroups) ? endpointGroups : [];
    const q = searchText.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((g) => (g.name || '').toLowerCase().includes(q));
  }, [endpointGroups, searchText]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filtered.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filtered.length / entriesPerPage) || 1;

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const parseEndpoints = ({ endpoint_type, endpointsText, subnetIds }) => {
    if (endpoint_type === 'subnet') {
      return subnetIds.filter(Boolean);
    }
    // For cidr/network/router/vlan: allow comma-separated values
    return endpointsText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const createForm = useFormik({
    initialValues: {
      name: '',
      description: '',
      endpoint_type: 'subnet',
      endpointsText: '',
      subnetIds: [],
      subnetSearch: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      endpoint_type: Yup.string().required('Endpoint type is required'),
    }),
onSubmit: async (values, { resetForm }) => {
  if (!projectId) return;

  const endpoints = parseEndpoints(values);

  if (!endpoints.length) {
    showErrorMessage('Please provide at least one endpoint');
    return;
  }

  setLoading(true);

  try {
    const response =
      await apiEndPoints.createVpnEndpointGroup(projectId, {
        name: values.name.trim(),
        description: values.description?.trim() || '',
        endpoint_type: values.endpoint_type,
        endpoints,
      });

    const data = response?.data || response;

    if (data?.statuscode && data.statuscode !== '00') {
      showErrorMessage(
        data?.statusmessage ||
          data?.message ||
          'Unable to create endpoint group',
      );
      return;
    }

    showSuccessMessage('Endpoint Group created');
    setCreateOpen(false);
    resetForm();
    await refetch();
  } catch (error) {
    const errorData =
      error?.response?.data ||
      error?.data ||
      error;

    showErrorMessage(
      errorData?.statusmessage ||
        errorData?.message ||
        error?.message ||
        'Unable to create endpoint group',
    );
  } finally {
    setLoading(false);
  }
},
  });

  const editForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selected?.name || '',
      description: selected?.description || '',
      endpoint_type: selected?.endpoint_type || 'subnet',
      endpointsText: Array.isArray(selected?.endpoints) ? selected.endpoints.join(', ') : '',
      subnetIds:
        selected?.endpoint_type === 'subnet' && Array.isArray(selected?.endpoints)
          ? selected.endpoints
          : [],
      subnetSearch: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      endpoint_type: Yup.string().required('Endpoint type is required'),
    }),
    onSubmit: async (values) => {
      if (!projectId || !selected?.id) return;
      const endpoints = parseEndpoints(values);
      if (!endpoints.length) {
        showErrorMessage('Please provide at least one endpoint');
        return;
      }
  setLoading(true);

    try {
      const response =
        await apiEndPoints.updateVpnEndpointGroup(
          projectId,
          selected.id,
          {
            name: values.name.trim(),
            description: values.description?.trim() || '',
            endpoint_type: values.endpoint_type,
            endpoints,
          },
        );

      const data = response?.data || response;

      if (data?.statuscode && data.statuscode !== '00') {
        showErrorMessage(
          data?.statusmessage ||
            data?.message ||
            'Unable to update endpoint group',
        );
        return;
      }

      showSuccessMessage('Endpoint Group updated');
      setEditOpen(false);
      setSelected(null);
      await refetch();
    } catch (error) {
      const errorData =
        error?.response?.data ||
        error?.data ||
        error;

      showErrorMessage(
        errorData?.statusmessage ||
          errorData?.message ||
          error?.message ||
          'Unable to update endpoint group',
      );
    } finally {
      setLoading(false);
    }
        },
  });

  const runDelete = async () => {
    if (!projectId || !confirmDelete.id) return;
    setLoading(true);
    try {
      await apiEndPoints.deleteVpnEndpointGroup(projectId, confirmDelete.id);
      showSuccessMessage('Endpoint Group deleted');
      setConfirmDelete({ open: false, id: '', name: '' });
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  const renderEndpointsInput = (form) => {
    if (form.values.endpoint_type !== 'subnet') {
      return (
        <TextArea
          label="Endpoints (comma-separated)"
          name="endpointsText"
          noOfRow="3"
          value={form.values.endpointsText}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          onError={form.touched.endpointsText ? form.errors.endpointsText : null}
          isLabelIcon={true}
          iconTitle="Remote peer subnet(s) address(es) with mask(s) in CIDR format separated with commas if needed (e.g. 20.1.0.0/24, 21.1.0.0/24). This field is valid if type is CIDR."
        />
      );
    }

    const q = form.values.subnetSearch.trim().toLowerCase();
    const filteredSubnets = subnetOptions.filter((s) => s.label.toLowerCase().includes(q));
    const selectedSet = new Set(form.values.subnetIds);

    return (
      <div className="grid gap-2">
        <Input
          label="Search subnets"
          name="subnetSearch"
          value={form.values.subnetSearch}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          placeholder="Type to filter subnet list..."
          isLabelIcon={true}
          iconTitle="Local subnet(s). This field is valid if type is Subnet."
        />
        <div className="max-h-56 overflow-auto rounded-md border border-[#c4c4c460] bg-white dark:bg-slate-800 p-2">
          {filteredSubnets.length === 0 ? (
            <div className="p-2 text-xs opacity-70">No subnets found.</div>
          ) : (
            filteredSubnets.map((s) => (
              <label
                key={s.value}
                className="flex items-center gap-2 p-2 text-xs cursor-pointer hover:bg-primaryBlue/5 rounded"
              >
                <input
                  type="checkbox"
                  checked={selectedSet.has(s.value)}
                  onChange={() => {
                    const next = selectedSet.has(s.value)
                      ? form.values.subnetIds.filter((id) => id !== s.value)
                      : [...form.values.subnetIds, s.value];
                    form.setFieldValue('subnetIds', next);
                  }}
                />
                <span className="truncate">{s.label}</span>
              </label>
            ))
          )}
        </div>
        <div className="text-xs opacity-70">
          Selected: {form.values.subnetIds.length}
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 md:px-10 py-6 min-h-screen">
      <Spinner loading={loading} />

      <p className="crimson-font text-xl font-medium text-primaryBlue mb-6">
        VPN Endpoint Groups
      </p>

      <div className="grid md:flex md:items-center md:justify-between gap-4 mb-4">
        <div className="w-full md:w-1/2 lg:w-1/3">
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search endpoint group..."
            className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
          />
        </div>
        <div
          onClick={() => setCreateOpen(true)}
          className="px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer w-fit"
        >
          Create endpoint group
        </div>
      </div>

      <div className="overflow-x-auto rounded-md dark:text-white">
        <table className="w-full text-sm text-left overflow-hidden">
          <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
            <tr>
              <th className="px-6 py-4 whitespace-nowrap">Name</th>
              <th className="px-6 py-4 whitespace-nowrap">Description</th>
              <th className="px-6 py-4 whitespace-nowrap">Type</th>
              <th className="px-6 py-4 whitespace-nowrap">Endpoints</th>
              <th className="px-6 py-4 whitespace-nowrap w-56"></th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 text-black/50 dark:text-white/50 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
            {(Array.isArray(currentEntries) ? currentEntries : []).map((row) => (
              <tr
                key={row.id || row.uuid || row.name}
                className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
              >
                <td className="text-start py-4 px-6 text-primaryBlue">{row.name || '—'}</td>
                <td className="text-start py-4 px-6">{row.description || '—'}</td>
                <td className="text-start py-4 px-6">{row.endpoint_type || '—'}</td>
                <td className="text-start py-4 px-6">
                  {Array.isArray(row.endpoints) ? row.endpoints.join(', ') : '—'}
                </td>
                <td className="text-start py-4 px-6 flex items-center gap-2">
                  <button
                    className="text-xs border rounded-md px-3 py-2 cursor-pointer"
                    onClick={() => {
                      setSelected(row);
                      setEditOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="text-xs border rounded-md px-3 py-2 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() =>
                      setConfirmDelete({
                        open: true,
                        id: row.id || row.uuid,
                        name: row.name || row.id || row.uuid,
                      })
                    }
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!filtered || filtered.length === 0) && (
              <tr>
                <td className="px-6 py-6 text-sm opacity-70" colSpan={5}>
                  No endpoint groups found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
            currentPage === 1 ? 'opacity-50' : 'opacity-100'
          }`}
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-sm text-primaryBlue dark:text-white">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
            currentPage === totalPages ? 'opacity-50' : 'opacity-100'
          }`}
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      <Modal
        title="Create VPN Endpoint Group"
        isVisible={createOpen}
        onClose={() => setCreateOpen(false)}
        size="md:w-[45%]"
      >
        <form className="grid gap-4" onSubmit={createForm.handleSubmit}>
          <Input
            label="Name"
            name="name"
            value={createForm.values.name}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            onError={createForm.touched.name ? createForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the VPN endpoint group."
          />
          <Input
            label="Description"
            name="description"
            value={createForm.values.description}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            isLabelIcon={true}
            iconTitle="A description for the VPN endpoint group."
          />
          <SearchableSelect
            label="Endpoint Type"
            name="endpoint_type"
            value={createForm.values.endpoint_type}
            onChange={(e) => {
              createForm.handleChange(e);
              // reset dependent inputs when type changes
              createForm.setFieldValue('endpointsText', '');
              createForm.setFieldValue('subnetIds', []);
              createForm.setFieldValue('subnetSearch', '');
            }}
            options={endpointTypeOptions}
            isLabelIcon={true}
            iconTitle="IPsec connection validation requires that local endpoints are subnets, and peer endpoints are CIDRs."
          />
          {renderEndpointsInput(createForm)}
          <div className="flex justify-end gap-2">
            <Button buttonText="Create" buttonRole="submit" />
          </div>
        </form>
      </Modal>

      <Modal
        title="Edit VPN Endpoint Group"
        isVisible={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        size="md:w-[45%]"
      >
        <form className="grid gap-4" onSubmit={editForm.handleSubmit}>
          <Input
            label="Name"
            name="name"
            value={editForm.values.name}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            onError={editForm.touched.name ? editForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the VPN endpoint group."
          />
          <Input
            label="Description"
            name="description"
            value={editForm.values.description}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            isLabelIcon={true}
            iconTitle="A description for the VPN endpoint group."
          />
          <SearchableSelect
            label="Endpoint Type"
            name="endpoint_type"
            value={editForm.values.endpoint_type}
            onChange={(e) => {
              editForm.handleChange(e);
              editForm.setFieldValue('endpointsText', '');
              editForm.setFieldValue('subnetIds', []);
              editForm.setFieldValue('subnetSearch', '');
            }}
            options={endpointTypeOptions}
            isLabelIcon={true}
            iconTitle="IPsec connection validation requires that local endpoints are subnets, and peer endpoints are CIDRs."
          />
          {renderEndpointsInput(editForm)}
          <div className="flex justify-end gap-2">
            <Button buttonText="Update" buttonRole="submit" />
          </div>
        </form>
      </Modal>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        name={confirmDelete.name}
        title="Delete Endpoint Group"
        description="Type the name exactly to confirm deletion"
        onClose={() => setConfirmDelete({ open: false, id: '', name: '' })}
        onConfirm={runDelete}
        isDeleting={loading}
      />
    </div>
  );
};

export default VpnEndpointGroups;


